import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "./eventFormPreview.css";
import EventTableBookingPreview from "./EventTableBookingPreview";

import { fetchClubCatalogItems } from "../../../../api/clubApi";
import CustomButton from "../../../customButton/customButton";
import { getCloudinaryMediaBaseUrl } from "../../../../config/cloudinaryBase";
import {
  formatCatalogMoney,
  unwrapCatalogList,
} from "../../../../helpers/clubCatalog";
import { htmlToPlainText, resolveCloudinaryImageSrc } from "../../../../helpers/helper.service";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatPreviewDate(input) {
  if (input == null || input === "") return "";
  const dateObject = new Date(input);
  if (Number.isNaN(dateObject.getTime())) return String(input);
  const day = dateObject.getDate();
  const month = MONTHS[dateObject.getMonth()];
  const year = dateObject.getFullYear();
  const hours = dateObject.getHours();
  const minutes = dateObject.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");
  return `${day} ${month} ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
}

function joinLabels(list) {
  const names = (Array.isArray(list) ? list : [])
    .map((item) => item?.label || item?.value || item?.name)
    .map((name) => String(name || "").trim())
    .filter(Boolean);
  return names.join(", ");
}

function yesNo(value, yes, no) {
  return value ? yes : no;
}

function displayOrDash(value) {
  const text = String(value ?? "").trim();
  return text || "—";
}

function imageSrc(localSrc, remoteSrc) {
  if (localSrc) return localSrc;
  if (!remoteSrc) return "";
  return resolveCloudinaryImageSrc(remoteSrc, getCloudinaryMediaBaseUrl());
}

function PreviewRow({ label, value }) {
  return (
    <div className="event-form-preview-row">
      <p className="event-form-preview-label">{label}</p>
      <p className="event-form-preview-value">{displayOrDash(value)}</p>
    </div>
  );
}

function PreviewBlock({ title, children }) {
  return (
    <section className="event-form-preview-block">
      <h3 className="event-form-preview-block-title">{title}</h3>
      {children}
    </section>
  );
}

function catalogRows(ids, items, overrides, stockQty) {
  const byId = new Map((items || []).map((item) => [String(item._id), item]));
  return (Array.isArray(ids) ? ids : []).map((id) => {
    const key = String(id);
    const item = byId.get(key);
    const override = overrides?.[key] ?? overrides?.[id];
    const stock = stockQty?.[key] ?? stockQty?.[id];
    return {
      id: key,
      name: item?.name || key,
      price:
        override != null && String(override).trim() !== ""
          ? formatCatalogMoney(override, item?.currency || "INR")
          : item
            ? formatCatalogMoney(item.unitPrice, item.currency)
            : "",
      stock:
        stock == null || String(stock).trim() === ""
          ? ""
          : String(stock),
    };
  });
}

const EventFormPreview = ({
  preview,
  isCreate,
  loading,
  onBack,
  onConfirm,
  showClubField,
  showTableBookingField,
  showStaffField,
  clubEmployeeOptions,
  catalogClubId,
  tableClubId,
  profileImage,
  daynamicImage,
  islayoutImage,
  daynamicLayoutImage,
  locationValue,
}) => {
  const { t } = useTranslation();
  const data = preview?.data || {};
  const labels = preview?.labels || {};
  const yes = t("EVENT_PREVIEW_YES");
  const no = t("EVENT_PREVIEW_NO");
  const [catalogItems, setCatalogItems] = useState([]);

  useEffect(() => {
    if (!catalogClubId) {
      setCatalogItems([]);
      return undefined;
    }
    let cancelled = false;
    fetchClubCatalogItems(catalogClubId)
      .then((res) => {
        if (cancelled) return;
        setCatalogItems(unwrapCatalogList(res).items || []);
      })
      .catch(() => {
        if (!cancelled) setCatalogItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogClubId]);

  const coverSrc = imageSrc(profileImage, daynamicImage);
  const layoutSrc = imageSrc(islayoutImage, daynamicLayoutImage);
  const aboutText = htmlToPlainText(data.about);
  const termsText = htmlToPlainText(data.terms);
  const staffNames = useMemo(() => {
    const selected = new Set(
      (Array.isArray(data.offlineAssignedUserIds)
        ? data.offlineAssignedUserIds
        : []
      ).map(String)
    );
    return (clubEmployeeOptions || [])
      .filter((option) => selected.has(String(option.id)))
      .map((option) => option.label)
      .join(", ");
  }, [clubEmployeeOptions, data.offlineAssignedUserIds]);

  const addons = catalogRows(
    data.addonItemIds,
    catalogItems,
    data.addonPriceOverrides,
    data.addonStockQty
  );
  const preorders = catalogRows(
    data.preorderItemIds,
    catalogItems,
    data.preorderPriceOverrides,
    data.preorderStockQty
  );
  const hike = data.tableBookingPriceHike;
  const autoHike = data.tableBookingAutoPriceHike;
  const tickets = Array.isArray(data.tickets) ? data.tickets : [];

  return (
    <Modal
      show
      onHide={onBack}
      scrollable
      backdrop={loading ? "static" : true}
      keyboard={!loading}
      className="event-preview-modal"
      dialogClassName="event-preview-modal__dialog"
    >
      <Modal.Header closeButton={!loading} className="event-preview-modal__header">
        <Modal.Title>{t("EVENT_PREVIEW_TITLE")}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="event-preview-modal__body">
      <p className="event-form-preview-hint">{t("EVENT_PREVIEW_HINT")}</p>

      <div className="event-preview-hero">
        <div
          className="event-preview-poster"
          style={{ width: 84, height: 112, flex: "0 0 84px", overflow: "hidden" }}
        >
          {coverSrc ? (
            <img
              src={coverSrc}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <span className="event-preview-poster-empty">{t("EVENT_PREVIEW_COVER")}</span>
          )}
        </div>
        <div className="event-preview-hero-copy">
          <h3>{displayOrDash(data.title)}</h3>
          <p>
            {displayOrDash(formatPreviewDate(data.eventstartDate))}
            {" – "}
            {displayOrDash(formatPreviewDate(data.eventEndDate))}
          </p>
        </div>
      </div>

      <PreviewBlock title={t("ADD_EVENT")}>
        <PreviewRow label={t("TITLE_LABLE")} value={data.title} />
        <PreviewRow
          label={t("EVENT_START_EVENT_TIMINGS")}
          value={formatPreviewDate(data.eventstartDate)}
        />
        <PreviewRow
          label={t("EVENT_END_EVENT_TIMINGS")}
          value={formatPreviewDate(data.eventEndDate)}
        />
        <PreviewRow
          label={t("BOOKING_START_TIME")}
          value={formatPreviewDate(data.bookingdate)}
        />
        <PreviewRow
          label={t("Booking End Time")}
          value={formatPreviewDate(data.bookingEnd)}
        />
        <PreviewRow label={t("LANGUAGE")} value={joinLabels(labels.languages)} />
        <PreviewRow label={t("GENERES")} value={joinLabels(labels.genres)} />
        <PreviewRow
          label={t("ASSIGN_ORGANISER")}
          value={labels.organiser?.label || data.organiser?.label}
        />
        {showClubField ? (
          <PreviewRow label={t("EVENT_CLUB_OPTIONAL")} value={data.club?.label} />
        ) : null}
        {showStaffField ? (
          <PreviewRow
            label={t("ASSIGN_EMPLOYEES_AND_OPERATORS")}
            value={staffNames}
          />
        ) : null}
        <PreviewRow label={t("ARTISTS_LABEL")} value={joinLabels(labels.artists)} />
        <PreviewRow label={t("CATEGORIES")} value={joinLabels(labels.categories)} />
      </PreviewBlock>

      <PreviewBlock title={t("LAYOUT_IMAGE")}>
        {layoutSrc ? (
          <div
            className="event-preview-layout"
            style={{ width: "100%", height: 140, overflow: "hidden" }}
          >
            <img
              src={layoutSrc}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>
        ) : (
          <p className="event-form-preview-value">—</p>
        )}
        <PreviewRow label={t("LAYOUT_DESCRIPTION")} value={data.layoutdesc} />
        <PreviewRow label={t("LOCATION")} value={data.map || locationValue} />
        <PreviewRow
          label={t("VENUE_TO_BE_ANNOUNCE")}
          value={yesNo(data.venuetobeanounced, yes, no)}
        />
      </PreviewBlock>

      <PreviewBlock title={t("ABOUT")}>
        <p className="event-form-preview-copy">{displayOrDash(aboutText)}</p>
      </PreviewBlock>

      <PreviewBlock title={t("TERMS")}>
        <p className="event-form-preview-copy">{displayOrDash(termsText)}</p>
      </PreviewBlock>

      <PreviewBlock title={t("EVENT_PREVIEW_OPTIONS")}>
        <PreviewRow
          label={t("SPONSORED_EVENTS")}
          value={yesNo(data.sponsored, yes, no)}
        />
        <PreviewRow label="18+ Event" value={yesNo(data.is18Plus, yes, no)} />
        <PreviewRow label={t("BOOKING_%")} value={data.bookingpercentage} />
        {showTableBookingField ? (
          <PreviewRow
            label={t("TABLE_BOOKING_FIXED_FEE")}
            value={data.tableBookingFixedFee}
          />
        ) : null}
      </PreviewBlock>

      <PreviewBlock title={t("ADD_TICKET")}>
        {tickets.length ? (
          <div className="event-form-preview-tickets">
            {tickets.map((ticket, index) => (
              <div className="event-form-preview-ticket" key={ticket._id || index}>
                <p className="event-form-preview-ticket-title">
                  {displayOrDash(ticket.tickettype)}
                </p>
                <PreviewRow label={t("TICKET_GST_PERCENT")} value={ticket.gstPercent} />
                <PreviewRow label={t("TICKET_PRICE")} value={ticket.ticketprice} />
                <PreviewRow
                  label={t("TOTAL_NUMBER_OF_TICKETS")}
                  value={ticket.totaltickets}
                />
                <PreviewRow
                  label={t("TICKET_PASSES_PER_UNIT")}
                  value={ticket.passesPerUnit}
                />
                <PreviewRow
                  label={t("TICKET_COVER_AMOUNT")}
                  value={ticket.coverAmount}
                />
                <PreviewRow
                  label={t("CATEGORY_DESCRIPTION")}
                  value={ticket.categorydesc}
                />
                <PreviewRow
                  label={t("TICKET_ONLINE_QUEUE_SOLD_OUT")}
                  value={yesNo(ticket.soldout, yes, no)}
                />
                <PreviewRow
                  label={t("TICKET_OFFLINE_SOLD_OUT")}
                  value={yesNo(ticket.offlineSoldOut, yes, no)}
                />
                {ticket.phasedPricingEnabled &&
                Array.isArray(ticket.phases) &&
                ticket.phases.length ? (
                  <div className="event-form-preview-phases">
                    {ticket.phases.map((phase, phaseIndex) => (
                      <p key={`${phase.name || "phase"}-${phaseIndex}`}>
                        {displayOrDash(phase.name)} · {t("TOTAL_NUMBER_OF_TICKETS")}{" "}
                        {phase.quantity} · {t("TICKET_PRICE")} {phase.price}
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="event-form-preview-value">—</p>
        )}
      </PreviewBlock>

      {showTableBookingField ? (
        <PreviewBlock title={t("EVENT_TABLE_BOOKING_TITLE")}>
          <EventTableBookingPreview
            clubId={tableClubId || data.club?.id}
            values={data}
          />
          {hike?.enabled ? (
            <PreviewRow
              label={t("EVENT_PREVIEW_PRICE_HIKE")}
              value={`${hike.hikeType} ${hike.hikeValue} · ${hike.scope}`}
            />
          ) : null}
          {autoHike?.enabled ? (
            <PreviewRow
              label={t("EVENT_PREVIEW_AUTO_HIKE")}
              value={`${autoHike.hikeType} ${autoHike.hikeValue} · ${autoHike.bookingsPerTier} · ${autoHike.scope}`}
            />
          ) : null}
        </PreviewBlock>
      ) : null}

      {catalogClubId ? (
        <PreviewBlock title={t("CATALOG_EVENT_SECTION")}>
          <p className="event-form-preview-subhead">{t("CATALOG_EVENT_ADDONS")}</p>
          {addons.length ? (
            addons.map((row) => (
              <PreviewRow
                key={row.id}
                label={row.name}
                value={[row.price, row.stock ? `${t("CATALOG_EVENT_STOCK_LABEL")} ${row.stock}` : ""]
                  .filter(Boolean)
                  .join(" · ")}
              />
            ))
          ) : (
            <p className="event-form-preview-value">—</p>
          )}
          <p className="event-form-preview-subhead">{t("CATALOG_EVENT_PREORDERS")}</p>
          {preorders.length ? (
            preorders.map((row) => (
              <PreviewRow
                key={row.id}
                label={row.name}
                value={[row.price, row.stock ? `${t("CATALOG_EVENT_STOCK_LABEL")} ${row.stock}` : ""]
                  .filter(Boolean)
                  .join(" · ")}
              />
            ))
          ) : (
            <p className="event-form-preview-value">—</p>
          )}
        </PreviewBlock>
      ) : null}

      </Modal.Body>
      <Modal.Footer className="event-preview-modal__footer">
        <CustomButton
          className="black-btn Fill_Button eventPrimaryBtn event-preview-modal__confirm"
          type="button"
          loading={loading}
          onClick={onConfirm}
        >
          {isCreate
            ? t("EVENT_PREVIEW_CONFIRM_CREATE")
            : t("EVENT_PREVIEW_CONFIRM_UPDATE")}
        </CustomButton>
        <CustomButton
          className="cancel-btn eventCancelBtn event-preview-modal__back"
          type="button"
          disabled={loading}
          onClick={onBack}
        >
          {t("EVENT_PREVIEW_BACK")}
        </CustomButton>
      </Modal.Footer>
    </Modal>
  );
};

export default EventFormPreview;
